// src/PartsSearch.js
import { useState, useEffect } from "react";
import { apiFetch } from "./api";

export default function PartsSearch({ site }) {
  const [filters, setFilters] = useState({
    part_number: "",
    description: "",
    location: "All",
    shelve: "",
    row: "",
    category: "",
  });

  const [locations, setLocations] = useState(["All"]);
  const [parts, setParts] = useState([]);
  const [err, setErr] = useState("");

  // 1) fetch locations for this site
  useEffect(() => {
    if (!site) return;

    let alive = true;
    setErr("");

    const params = new URLSearchParams({ site });

    apiFetch(`/api/parts/locations?${params}`)
      .then((r) => {
        if (!r.ok) throw new Error(`Locations fetch failed (${r.status})`);
        return r.json();
      })
      .then((data) => {
        if (!alive) return;
        setLocations(["All", ...(data || [])]);
      })
      .catch((e) => {
        console.error(e);
        if (alive) setErr("Failed to load locations");
      });

    return () => {
      alive = false;
    };
  }, [site]);

  // 2) fetch parts whenever filters or site change
  useEffect(() => {
    if (!site) return;

    let alive = true;
    setErr("");

    const params = new URLSearchParams({ site, ...filters });

    apiFetch(`/api/parts/search?${params}`)
      .then((r) => {
        if (!r.ok) throw new Error(`Parts fetch failed (${r.status})`);
        return r.json();
      })
      .then((data) => {
        if (!alive) return;
        setParts(data || []);
      })
      .catch((e) => {
        console.error(e);
        if (alive) setErr("Failed to load parts");
      });

    return () => {
      alive = false;
    };
  }, [filters, site]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  return (
    <div className="p-5">
      <h2 className="text-2xl font-semibold mb-4">Search Parts @ {site}</h2>

      {err && <p className="mb-3 text-red-600">{err}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        {[
          ["part_number", "Part #"],
          ["description", "Description"],
          ["location", "Location"],
          ["shelve", "Shelve"],
          ["row", "Row"],
          ["category", "Category"],
        ].map(([key, label]) =>
          key === "location" ? (
            <select
              key={key}
              name={key}
              value={filters[key]}
              onChange={onChange}
              className="border rounded px-2 py-1"
            >
              {locations.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          ) : (
            <input
              key={key}
              name={key}
              value={filters[key]}
              placeholder={label}
              onChange={onChange}
              className="border rounded px-2 py-1"
            />
          )
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {[
                "Part Number",
                "Description",
                "Location",
                "Shelve",
                "Row",
                "Total Quantity",
                "Manufacturer",
                "Category",
              ].map((h) => (
                <th key={h} className="border p-2 bg-gray-100 text-left">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {parts.map((p) => (
              <tr key={p["Part Number"]} className="hover:bg-gray-50">
                <td className="border p-2 font-mono">{p["Part Number"]}</td>
                <td className="border p-2">{p.Description}</td>
                <td className="border p-2">{p.Location}</td>
                <td className="border p-2">{p.Shelve}</td>
                <td className="border p-2">{p.Row}</td>
                <td className="border p-2">{p["Total Quantity"]}</td>
                <td className="border p-2">{p.Manufacturer}</td>
                <td className="border p-2">{p.Category}</td>
              </tr>
            ))}
            {parts.length === 0 && (
              <tr>
                <td className="border p-2" colSpan={8}>
                  No results.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
